import { Link, Text, Icon } from '@nike/eds'
import { BackButton } from '../../../common/components/backbutton/BackButton'
import { ShipmentLink } from '../../../common/components/links/Links'
import { useQuery, gql } from '@apollo/client'
import { useParams } from 'react-router-dom'
import { ErrorSnack } from '../../../common/components/errorsnack/ErrorSnack'
import { SimpleTable } from '../../../common/components/table/SimpleTable'
import { get } from '../../../common/components/properties/Properties'
import sortBy from 'lodash.sortby'
import { useMemo } from 'react'
import { date } from '../../../common/utils/formatter'
import { downloadS3Payload } from '../../../services/DownloadService'

const GetShipmentEventSourcesDocument = gql`
  query getShipmentEventSources($packlistNumber: String!) {
    getShipment(packlistNumber: $packlistNumber) {
      event_sources {
        s3Key
        processedTimestamp
      }
    }
    getGoodsHoldersByPacklist(packlistNumber: $packlistNumber) {
      goodsHolderNumber
      event_sources {
        s3Key
        processedTimestamp
      }
    }
    getTrackAndTraceByPacklist(packlistNumber: $packlistNumber) {
      event_sources {
        s3Key
        processedTimestamp
      }
    }
  }
`

function logsUrl(search: object) {
  const searchParams = new URLSearchParams([
    ['earliest', '-30d'],
    ['latest', 'now'],
    [
      'q',
      `search ${Object.entries({
        index: process.env.REACT_APP_LOGS_INDEX,
        ...search,
      })
        .map(([k, v]) => `${k}="${v}"`)
        .join(' ')}`,
    ],
  ])
  return `${process.env.REACT_APP_LOGS_URL}?${searchParams}`
}

export function ShipmentEventsPage() {
  let { packlistNumber } = useParams()

  const { loading, error, data } = useQuery(GetShipmentEventSourcesDocument, {
    variables: { packlistNumber },
  })

  const sorted = (arr) => sortBy(arr, 'processedTimestamp')
  const shipmentEvents = useMemo(
    () => (data ? sorted(data.getShipment?.event_sources) : []),
    [data]
  )
  const goodsHolderEvents = useMemo(
    () =>
      data
        ? sortBy(data.getGoodsHoldersByPacklist, 'goodsHolderNumber').map((gh) => ({
            goodsHolderNumber: gh.goodsHolderNumber,
            events: sorted(gh.event_sources),
          }))
        : [],
    [data]
  )
  const trackAndTraceEvents = useMemo(
    () => (data ? sorted(get(data.getTrackAndTraceByPacklist, 'event_sources').flat()) : []),
    [data]
  )

  if (loading) {
    return <p>Loading ...</p>
  }
  if (error) {
    return <ErrorSnack error={error} />
  }

  const columns = [
    {
      Header: 'Timestamp',
      accessor: (c) => date(c.processedTimestamp),
    },
    {
      Header: 'S3 Key',
      accessor: (cell) => cell,
      Cell: ({ value }) => {
        if (value.s3Key) {
          return (
            <>
              {value.s3Key}
              <Link onClick={() => downloadS3Payload(value.s3Key)} title="Download payload">
                <Icon name="Download" />
              </Link>
              <Link
                href={logsUrl({
                  appName: process.env.REACT_APP_LOGS_SHIPMENT_TRACKING_APP_NAME,
                  s3Key: value.s3Key,
                })}
                target="_blank"
                title="Logs"
              >
                <Icon name="Feed" />
              </Link>
            </>
          )
        } else {
          return '-'
        }
      },
    },
  ]

  return (
    <div className="column-layout--row-gap-24">
      <title className="title">
        <BackButton />
        <Text font="display-4" as="h1">
          <ShipmentLink packlistNumber={packlistNumber} className="inherit-text-color">
            Packlist {packlistNumber}{' '}
          </ShipmentLink>
          <Link href={logsUrl({ packlistNumber })} target="_blank" title="Logs">
            <Icon name="Feed" />
          </Link>
        </Text>
      </title>
      <SimpleTable title="Shipment Event Sources" columns={columns} data={shipmentEvents} />
      {goodsHolderEvents.map(({ goodsHolderNumber, events }) => (
        <>
          <Text as="h2" font="title-3">
            {`Carton ${goodsHolderNumber} Event Sources`}{' '}
            <Link href={logsUrl({ cartonNumber: goodsHolderNumber })} target="_blank" title="Logs">
              <Icon name="Feed" />
            </Link>
          </Text>
          <SimpleTable key={goodsHolderNumber} columns={columns} data={events} />
        </>
      ))}

      <SimpleTable
        title="Track and Trace Event Sources"
        columns={columns}
        data={trackAndTraceEvents}
      />
    </div>
  )
}
